import { create } from 'zustand';
import { MOCK_DEPLOYMENTS, MOCK_USERS } from '@/deploy/data/mock';
import type {
  Deployment,
  DeploymentStatus,
  DeploymentStep,
  EnvironmentId,
} from '@/deploy/types';

interface StartDeploymentInput {
  projectId: string;
  source: EnvironmentId;
  target: EnvironmentId;
  kind: 'container' | 'artifact' | 'api' | 'resource-pack';
  items: string[];
  includedServices: EnvironmentId[];
  restartAfter: boolean;
  scheduledAt?: string;
  note?: string;
}

interface DeploymentsState {
  deployments: Deployment[];
  activeDeploymentId: string | null;
  startDeployment: (input: StartDeploymentInput) => string;
  tick: () => void;
}

const baseSteps = (restart: boolean): DeploymentStep[] => {
  const steps: DeploymentStep[] = [
    { id: 's-bump',    label: 'Bump version release-version.json', status: 'queued' },
    { id: 's-build',   label: 'Build image Docker container',       status: 'queued' },
    { id: 's-push',    label: 'Push sur registry',                   status: 'queued' },
    { id: 's-ptero',   label: 'Mise à jour egg Pterodactyl',         status: 'queued' },
  ];
  if (restart) steps.push({ id: 's-restart', label: 'Redémarrage des instances', status: 'queued' });
  return steps;
};

export const useDeploymentsStore = create<DeploymentsState>((set, get) => ({
  deployments: MOCK_DEPLOYMENTS,
  activeDeploymentId: null,

  startDeployment: (input) => {
    const id = `d-${Date.now().toString(36)}`;
    const now = new Date().toISOString();
    const deployment: Deployment = {
      id,
      projectId: input.projectId,
      source: input.source,
      target: input.target,
      kind: input.kind,
      items: input.items,
      includedServices: input.includedServices,
      status: 'running',
      author: MOCK_USERS[0],
      startedAt: now,
      restartAfter: input.restartAfter,
      scheduledAt: input.scheduledAt,
      note: input.note,
      steps: baseSteps(input.restartAfter),
    };
    // 1ère étape immédiatement "running"
    deployment.steps[0].status = 'running';
    deployment.steps[0].startedAt = now;

    set({
      deployments: [deployment, ...get().deployments],
      activeDeploymentId: id,
    });
    return id;
  },

  // Simulation très simple : on avance l'étape courante d'un cran à chaque tick.
  tick: () => {
    const { activeDeploymentId, deployments } = get();
    if (!activeDeploymentId) return;

    const idx = deployments.findIndex((d) => d.id === activeDeploymentId);
    if (idx === -1) return;

    const current = deployments[idx];
    if (current.status !== 'running') return;

    const stepIdx = current.steps.findIndex((s) => s.status === 'running');
    if (stepIdx === -1) return;

    const now = new Date().toISOString();
    const updatedSteps = [...current.steps];
    updatedSteps[stepIdx] = { ...updatedSteps[stepIdx], status: 'success', finishedAt: now };

    const nextIdx = stepIdx + 1;
    let newStatus: DeploymentStatus = current.status;
    let finishedAt: string | undefined;
    let durationSec: number | undefined;

    if (nextIdx < updatedSteps.length) {
      updatedSteps[nextIdx] = { ...updatedSteps[nextIdx], status: 'running', startedAt: now };
    } else {
      newStatus = 'success';
      finishedAt = now;
      durationSec = Math.round(
        (new Date(finishedAt).getTime() - new Date(current.startedAt).getTime()) / 1000,
      );
    }

    const next = [...deployments];
    next[idx] = {
      ...current,
      steps: updatedSteps,
      status: newStatus,
      finishedAt,
      durationSec,
    };

    set({
      deployments: next,
      activeDeploymentId: newStatus === 'running' ? activeDeploymentId : null,
    });
  },
}));
